<template>
  <BasePage :back-button="true" :pageTitle="__DICT__.actionMenu.loadTransfer" show-menu show-footer>
    <Card margin="">
      <template #header>
        <ul class="nav nav-tabs card-header-tabs me-2">
          <li class="nav-item">
            <a
                class="nav-link"
                :class="{ 'active': activeTab === 'airport' }"
                href="#"
                @click.prevent="activeTab = 'airport'"
            >
              {{ __DICT__.airport }}
            </a>
          </li>
          <li class="nav-item">
            <a
                class="nav-link"
                :class="{ 'active': activeTab === 'outbound' }"
                href="#"
                @click.prevent="activeTab = 'outbound'"
            >
              {{ __DICT__.outbound }}
            </a>
          </li>
          <li class="nav-item">
            <a
                class="nav-link"
                :class="{ 'active': activeTab === 'inbound' }"
                href="#"
                @click.prevent="activeTab = 'inbound'"
            >
              {{ __DICT__.inbound }}
            </a>
          </li>
        </ul>
      </template>
      <div v-if="activeTab === 'inbound'" class="list-group list-group-flush">

        <router-link
            v-for="transfer in inboundTransfers"
            :key="transfer"
            :to="transferLink(transfer,'inbound')"
            class="list-group-item list-group-item-action"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              {{ transfer }}
              <div class="text-muted small">
                {{ formatFirstDeparture(getTransferTransportInfo(transfer)) }}
              </div>
            </div>
          </div>
        </router-link>
        <div v-if="inboundTransfers.length === 0" class="text-center py-3 text-muted">
          {{ __DICT__.selectTransfer.noInboundTransfers }}
        </div>
      </div>
      <div v-else-if="activeTab === 'airport'" class="list-group list-group-flush">
        <!-- Airport Selection -->
        <template v-if="!selectedAirport">
          <a
              v-for="airport in airportList"
              :key="airport.systemId"
              href="#"
              class="list-group-item list-group-item-action"
              @click.prevent="selectAirport(airport)"
          >
            {{ airport.location }}
          </a>
          <div v-if="airportList.length === 0" class="text-center py-3 text-muted">
            {{ __DICT__.selectTransfer.noAirportsAvailable }}
          </div>
        </template>
        <!-- Departures board (TL-05), behind the transfer-run-status flag -->
        <DeparturesBoard
            v-else-if="boardEnabled"
            :week-id="weekStore.systemId"
            :airport="selectedAirport"
            @change-airport="selectedAirport = null"
        />
        <!-- Airport Transfers List -->
        <template v-else>
          <div class="list-group-item d-flex justify-content-between align-items-center" style="background-color: rgba(var(--tblr-body-color-rgb), 0.04)">
            <strong>{{ selectedAirport.location }}</strong>
            <button class="btn btn-sm btn-outline-secondary" @click="selectedAirport = null">
              {{ __DICT__.change }}
            </button>
          </div>
          <router-link
              v-for="transfer in airportTransfers"
              :key="transfer.transferRef"
              :to="{ path: `/loadTransferNew/${transfer.transferRef}/outbound`, query: { weekId: transfer.weekId } }"
              class="list-group-item list-group-item-action"
          >
            <div class="d-flex justify-content-between align-items-center">
              <span>{{ transfer.transferRef }}</span>
              <span
                class="badge text-white"
                :style="{ backgroundColor: getResortColor(transfer.resort) }"
              >
                {{ transfer.name }}
              </span>
            </div>
          </router-link>
          <div v-if="airportTransfers.length === 0" class="text-center py-3 text-muted">
            {{ __DICT__.selectTransfer.noTransfersForAirport }}
          </div>
        </template>
      </div>
      <div v-else class="list-group list-group-flush">
        <router-link
            v-for="transfer in outboundTransfers"
            :key="transfer"
            :to="transferLink(transfer,'outbound')"
            class="list-group-item list-group-item-action"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              {{ transfer }}
              <div class="text-muted small">
                {{ formatFirstDeparture(getTransferTransportInfo(transfer)) }}
              </div>
            </div>
          </div>
        </router-link>
        <div v-if="outboundTransfers.length === 0" class="text-center py-3 text-muted">
          {{ __DICT__.selectTransfer.noOutboundTransfers }}
        </div>
      </div>
    </Card>
  </BasePage>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { api } from "@/services/api.js";
import BasePage from "@/components/BasePage.vue";
import { useWeekStore } from "@/stores/week.js";
import { Card } from "@/components/atomic";
import DeparturesBoard from "@/components/DeparturesBoard.vue";
import { useConfigFlag } from "@/composables/useConfigFlag.js";

const { enabled: boardEnabled } = useConfigFlag('transfer-run-status')

const weekStore = useWeekStore()
const route = useRoute()
const activeTab = ref('airport');
const inboundTransfers = ref([]);
const outboundTransfers = ref([]);
const weekData = ref([]);
const airportList = ref([]);
const airportTransfers = ref([]);
const selectedAirport = ref(null);

const resortColors = [
  '#206bc4', '#4299e1', '#7c3aed', '#f76707', '#2fb344',
  '#d63939', '#f59f00', '#ae3ec9', '#17a2b8', '#0ca678',
  '#d6336c', '#1e90ff', '#ff6b6b', '#51cf66', '#845ef7',
  '#ff922b', '#20c997', '#e64980', '#339af0', '#38d9a9',
  '#be4bdb', '#12b886', '#fd7e14', '#5c7cfa', '#fab005'
]

const getResortColor = (resortId) => {
  const id = parseInt(resortId) || 0
  return resortColors[Math.abs(id) % resortColors.length]
}

const selectAirport = async (airport) => {
  selectedAirport.value = airport;
  airportTransfers.value = await api.get(`/api/airport/${weekStore.systemId}/${airport.systemId}`);
}

const transferLink = (transfer, direction) => `/loadTransferNew/${transfer}/${direction}`

const transferPassengers = computed(() => {
  const transfers = {}
  const data = weekData.value || []
  data.forEach(passenger => {
    const transport = passenger.allocPlaces?.find(place =>
        place.transport?.transportType === "transfer"
    )?.transport

    if (transport?.coach) {
      if (!transfers[transport.coach]) {
        transfers[transport.coach] = {
          passengers: [],
          transportInfo: transport
        }
      }
      transfers[transport.coach].passengers.push(passenger)
    }
  })
  return transfers
})

function getPassengersForTransfer(transferId) {
  return transferPassengers.value[transferId]?.passengers || []
}

function getTransferTransportInfo(transferId) {
  return transferPassengers.value[transferId]?.transportInfo
}

function formatFirstDeparture(transportInfo) {
  if (!transportInfo?.departures?.length) return ''

  const firstDeparture = transportInfo.departures.sort((a, b) =>
      new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
  )[0]
  const date = new Date(firstDeparture.dateTime)

  return `${firstDeparture.location} - ${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB')}`
}

onMounted(async () => {
  try {
    // Load both transfers list and week data
    const [transfers, airport, data] = await Promise.all([
      api.get(`/api/transfer/allTransfers/${weekStore.weekId}`),
      api.get(`/api/airport/${weekStore.systemId}`),
      weekStore.weekData
    ]);

    inboundTransfers.value = transfers.inbound;
    outboundTransfers.value = transfers.outbound;
    airportList.value = airport

    // My Week links here with ?airport=<GBS location id> (TL-11).
    const preselect = route.query.airport
    if (preselect) {
      const match = airportList.value.find(a => String(a.systemId) === String(preselect))
      if (match) await selectAirport(match)
    }
    weekData.value = data;
  } catch (error) {
    console.error('Error loading data:', error);
  }
});
</script>